/**
 * Forum Stats Component
 * 
 * Displays modern forum statistics with icons and animations
 */

import React from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Users, MessageCircle, TrendingUp, Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next';

interface ForumStatsProps {
  usersOnline?: number
  totalTopics?: number
  totalPosts?: number
  newestMember?: string
}

export default function ForumStats({
  usersOnline = 0,
  totalTopics = 0,
  totalPosts = 0,
  newestMember = 'N/A',
}: ForumStatsProps) {
  const { t } = useTranslation();
  /*
    Os quatro cartões usam a **mesma** cor, a primária da loja.

    Vinham de quatro tokens diferentes, e dois deles (`warning` e `success`)
    nem são configuráveis pelo lojista: são cores de estado, fixas no tema. Um
    contador de posts não é alerta.

    Cheguei a alternar entre primária e destaque para manter variedade, e ficou
    pior: são quatro cartões iguais lado a lado, e cores diferentes sugerem uma
    distinção que não existe entre eles.
  */
  const stats = [
    {
      icon: Users,
      label: t('store.forum.statsOnline'),
      value: usersOnline,
      color: 'text-primary',
      bg: 'bg-primary/10',
      gradient: 'from-primary/20 to-primary/5',
    },
    {
      icon: MessageCircle,
      label: t('store.forum.statsTopics'),
      value: totalTopics.toLocaleString(),
      color: 'text-primary',
      bg: 'bg-primary/10',
      gradient: 'from-primary/20 to-primary/5',
    },
    {
      icon: TrendingUp,
      label: t('store.forum.statsPosts'),
      value: totalPosts.toLocaleString(),
      color: 'text-primary',
      bg: 'bg-primary/10',
      gradient: 'from-primary/20 to-primary/5',
    },
    {
      icon: Clock,
      label: t('store.forum.statsNewestMember'),
      value: newestMember,
      color: 'text-primary',
      bg: 'bg-primary/10',
      gradient: 'from-primary/20 to-primary/5',
    },
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {stats.map((stat, index) => {
        const Icon = stat.icon
        return (
          <Card
            key={index}
            className="relative overflow-hidden border-card-border hover:border-primary/50 transition-all duration-300 hover:shadow-glow group"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
            <CardContent className="p-6 relative">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 ${stat.bg} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className={`w-7 h-7 ${stat.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-muted-foreground font-medium mb-1">
                    {stat.label}
                  </p>
                  <p className="text-2xl font-bold text-foreground truncate">
                    {stat.value}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
